import {useMemo} from 'react';

import {Box, Dialog, DialogContent, Typography} from '@mui/material';
import Lottie from 'lottie-react';
import {useTranslation} from 'react-i18next';

import FuseSvgIcon from '@/@fuse/core/FuseSvgIcon/FuseSvgIcon';
import {PaymentLinkError} from '@/app/main/paymentLink/components';
import {PaymentLinkLogo} from '@/app/main/paymentLink/components/PaymentLinkLogo/PaymentLinkLogo';
import {PaymentLinkPromo} from '@/app/main/paymentLink/components/PaymentLinkPromo/PaymentLinkPromo';
import {PaymentLinkUtm} from '@/app/main/paymentLink/models/constants/paymentLink.constants';
import {AppButton} from '@/app/shared-components/AppButton/AppButton';
import {AppButtonVariant} from '@/app/shared-components/AppButton/models/interfaces';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import ModakLoaderAnimation from '@/lotties/ModakLoader.json';
import {COLORS} from '@/styles/colors';

import {useViewModelProvider, ViewModelProvider} from './ViewModelContext';

const PaymentLinkOutcomeContent = () => {
  const {t} = useTranslation();
  const {isMobile} = useWindowSize();
  const {
    checkoutResultData,
    showModalPending,
    setShowModalPending,
    isSinglePayment,
  } = useViewModelProvider();
  const showInfoScreen = useMemo(() => {
    return (
      checkoutResultData?.status === 'funds_transferred' ||
      checkoutResultData?.status === 'declined'
    );
  }, [checkoutResultData]);
  return (
    <section className="payment-link-background">
      {showInfoScreen && (
        <Box className="payment-link-container">
          <PaymentLinkLogo />
          <>
            {checkoutResultData && (
              <Box className="payment-link-image-text-container">
                <Box className="payment-link-text-container">
                  {checkoutResultData?.status === 'funds_transferred' ? (
                    <FuseSvgIcon
                      size={40}
                      sx={{color: COLORS.CONTENT_POSITIVE}}>
                      heroicons-solid:check-circle
                    </FuseSvgIcon>
                  ) : (
                    <FuseSvgIcon
                      size={40}
                      sx={{color: COLORS.CONTENT_WARNING}}>
                      heroicons-solid:x-circle
                    </FuseSvgIcon>
                  )}

                  <Typography
                    className={`payment-link-title ${
                      checkoutResultData?.status === 'funds_transferred'
                        ? 'payment-link-title--success'
                        : 'payment-link-title--declined'
                    }`}>
                    {checkoutResultData?.status === 'funds_transferred'
                      ? t('payment_link_outcome.title_success', {
                          name: checkoutResultData?.receiver_user.name,
                          amount:
                            checkoutResultData.amount_without_fee.toFixed(2),
                        })
                      : t('payment_link_outcome.title_declined')}
                  </Typography>
                  <Typography className="payment-link-subtitle">
                    {checkoutResultData?.status === 'funds_transferred'
                      ? t('payment_link_outcome.sub_title_success')
                      : t('payment_link_outcome.sub_title_declined')}
                  </Typography>
                </Box>
              </Box>
            )}
            <PaymentLinkPromo
              utmCampaign={
                isSinglePayment
                  ? PaymentLinkUtm.Campaign.LINK
                  : PaymentLinkUtm.Campaign.QR
              }
              utmMedium={PaymentLinkUtm.Medium.Success}
            />
          </>
        </Box>
      )}
      {checkoutResultData?.status === 'pending' && !showModalPending && (
        <Box className="payment-link-container">
          <PaymentLinkLogo />
          <>
            {checkoutResultData && (
              <Box className="payment-link-image-text-container">
                <Box className="payment-link-text-container">
                  <FuseSvgIcon
                    size={40}
                    sx={{color: COLORS.CONTENT_WARNING}}>
                    heroicons-solid:exclamation-circle
                  </FuseSvgIcon>
                  <Typography className="payment-link-title payment-link-title--declined">
                    {t('payment_link_outcome.pending_title')}
                  </Typography>
                  <Typography className="payment-link-subtitle">
                    {t('payment_link_outcome.pending_sub_title')}
                  </Typography>

                  <Box
                    sx={{
                      paddingBottom: isMobile ? '16px' : '0',
                      width: isMobile ? '100%' : 'fit-content',
                    }}>
                    <AppButton
                      onClick={() => {
                        window.location.reload();
                      }}
                      label={t('payment_link_outcome.check_updates')}
                      variant={AppButtonVariant.onHelp}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            <PaymentLinkPromo
              utmCampaign={
                isSinglePayment
                  ? PaymentLinkUtm.Campaign.LINK
                  : PaymentLinkUtm.Campaign.QR
              }
              utmMedium={PaymentLinkUtm.Medium.Success}
            />
          </>
        </Box>
      )}
      {checkoutResultData?.status === 'error' && (
        <Box className="payment-link-container">
          <>
            <PaymentLinkError />
          </>
        </Box>
      )}
      <Dialog
        open={showModalPending}
        onClose={() => {
          setShowModalPending(false);
        }}
        fullWidth
        maxWidth={isMobile ? false : 'sm'}
        PaperProps={{
          sx: {
            maxWidth: '700px',
            width: '700px',
          },
          style: isMobile
            ? {
                width: '90%',
                margin: 0,
                maxWidth: 'none',
              }
            : {},
        }}>
        <DialogContent>
          <Box className="payment-link-outcome-modal">
            <Lottie
              animationData={ModakLoaderAnimation}
              loop
              className="modak-loader"
            />
            <Typography className="payment-link-outcome-modal-title">
              {t('payment_link_outcome.modal_title')}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </section>
  );
};

const PaymentLinkOutcomePage = () => (
  <ViewModelProvider>
    <PaymentLinkOutcomeContent />
  </ViewModelProvider>
);

export default PaymentLinkOutcomePage;
